import { assertIsNotUndefined } from '@gitbook/util-ts';

import { _valueOf, debugPerformance, isPrimitive, type JSPrimitive } from '../utils';
import { createFinalizationRegistry } from './gc';
import { smi } from './math';
import { IS_WEAK_HASHING_SUPPORTED } from './support';

let currentObjHashUID = 0;

const hashObjectCache = new WeakMap<object, number>();
const hashSymbolsCache = new Map<symbol, number>();
const hashPrimitiveCache = new Map<JSPrimitive, [number, WeakRef<any>[]]>();
const staticHashPrimitiveCache = new Map<JSPrimitive, number>();

const STRING_HASH_CACHE_MIN_STRLEN = 16;
const STRING_HASH_CACHE_MAX_SIZE = 255;

/**
 * Compute a hash for a JS value (primitive or objects).
 * For objects and symbols, it works by assigning an incremented hash numbe.
 * For primitive values, we compress the values into a int hash.
 *
 * To avoid infinite growing memory, it observes the garbage collector and remove the hash of values
 * used by collected objects.
 *
 * This hash should be consistent in an execution, but should not be preserved as it yields different result
 * between executions for objects (It doesn't use the value of nested objects but instead cache the object).
 */
export function hashJSValue(
    /**
     * Parent object owning this `input`. When the parent object is garbage collected, the hash of the
     * `input` can be removed from the cache (depending on the JS engine garbage collector).
     */
    parentObject: object,
    /**
     * JS value to hash.
     */
    input: any
): number {
    if (isPrimitive(input)) {
        return hashPrimitive(parentObject, input);
    }

    const value = _valueOf(input);
    if (isPrimitive(value)) {
        return hashPrimitive(parentObject, value);
    }
    return hashObject(value);
}

const registry = createFinalizationRegistry<JSPrimitive>((value) => {
    garbageCollectionPrimitiveHash(value);
});

/**
 * Allocate a hash for a primitive value.
 */
function hashPrimitive(parentObject: object, value: JSPrimitive): number {
    if (value === null || value === undefined) {
        // @ts-ignore - not sure why the tsc ClI complains about this
        return hashNullish(value);
    }

    if (typeof value === 'boolean') {
        // The hash values for built-in constants are a 1 value for each 5-byte
        // shift region expect for the first, which encodes the value. This
        // reduces the odds of a hash collision for these common values.
        return value ? 0x42108421 : 0x42108420;
    }

    if (typeof value === 'number') {
        return hashNumber(value);
    }

    if (typeof value === 'symbol') {
        return hashSymbol(value);
    }

    if (value.length < STRING_HASH_CACHE_MIN_STRLEN) {
        return hashString(value);
    }

    // Support for environment that do not support WeakRef
    // or in Node.js where we want to use a more direct cache because references are short living.
    // @ts-ignore - util-immutable can run in a TS codebase without lib: dom
    if (!IS_WEAK_HASHING_SUPPORTED || typeof window === 'undefined') {
        if (staticHashPrimitiveCache.has(value)) {
            const hash = staticHashPrimitiveCache.get(value);
            assertIsNotUndefined(hash);
            return hash;
        }

        const hashed = hashString(value);

        if (staticHashPrimitiveCache.size > STRING_HASH_CACHE_MAX_SIZE) {
            staticHashPrimitiveCache.clear();
        }

        staticHashPrimitiveCache.set(value, hashed);

        return hashed;
    }

    registry?.register(parentObject, value);

    if (hashPrimitiveCache.has(value)) {
        const hashPrimitive = hashPrimitiveCache.get(value);
        assertIsNotUndefined(hashPrimitive);
        const [currentHash, refs] = hashPrimitive;
        refs.push(new WeakRef(parentObject));
        return currentHash;
    }

    const hashed = hashString(value);

    if (hashPrimitiveCache.size > STRING_HASH_CACHE_MAX_SIZE) {
        hashPrimitiveCache.clear();
    }

    hashPrimitiveCache.set(value, [hashed, [new WeakRef([parentObject])]]);

    debugPerformance.hashValuesTotalCount++;
    debugPerformance.hashValuesCount = hashPrimitiveCache.size;

    return hashed;
}

/**
 * Allocate a new hash for a symbol.
 * This cache can grow indefinitely but the app should have a limited set of symbols.
 */
function hashSymbol(symbol: symbol): number {
    if (hashSymbolsCache.has(symbol)) {
        const hashSymbol = hashSymbolsCache.get(symbol);
        assertIsNotUndefined(hashSymbol);
        return hashSymbol;
    }

    const hashed = nextHash();
    hashSymbolsCache.set(symbol, hashed);

    return hashed;
}

/**
 * This is the hash from JVM
 * The hash code for a string is computed as
 * s[0] * 31 ^ (n - 1) + s[1] * 31 ^ (n - 2) + ... + s[n - 1],
 * where s[i] is the ith character of the string and n is the length of
 * the string. We "mod" the result to make it between 0 (inclusive) and 2^31
 * (exclusive) by dropping high bits.
 */
function hashString(string: string): number {
    let hashed = 0;
    for (let ii = 0; ii < string.length; ii++) {
        hashed = (31 * hashed + string.charCodeAt(ii)) | 0;
    }
    return smi(hashed);
}

// Compress arbitrarily large numbers into smi hashes.
function hashNumber(n: number): number {
    if (n !== n || n === Number.POSITIVE_INFINITY) {
        return 0;
    }
    let hash = n | 0;
    if (hash !== n) {
        hash ^= n * 0xffffffff;
    }
    while (n > 0xffffffff) {
        n /= 0xffffffff;
        hash ^= n;
    }
    return smi(hash);
}

function hashNullish(nullish: null | undefined): number {
    return nullish === null ? 0x42108422 : /* undefined */ 0x42108423;
}

/**
 * Hash a native JS object.
 */
function hashObject(obj: object): number {
    if (hashObjectCache.has(obj)) {
        const value = hashObjectCache.get(obj);
        assertIsNotUndefined(value);
        return value;
    }

    const hashed = nextHash();
    hashObjectCache.set(obj, hashed);

    return hashed;
}

/**
 * Allocate a new hash.
 */
function nextHash(): number {
    const nextHash = ++currentObjHashUID;
    if (currentObjHashUID & 0x40000000) {
        currentObjHashUID = 0;
    }
    return nextHash;
}

/**
 * Check if all parents of a primitive value have been collected
 * and collect the primitive hash entry if so.
 */
function garbageCollectionPrimitiveHash(value: JSPrimitive) {
    const entry = hashPrimitiveCache.get(value);
    if (!entry) {
        return;
    }

    const [, refs] = entry;

    while (refs.length) {
        const ref = refs[0];
        if (ref?.deref()) {
            // The object is still alive, we can stop here.
            return;
        }

        // The object has been collected, we can remove it from the list.
        refs.shift();
    }

    // All objects have been collected, we can remove the entry.
    hashPrimitiveCache.delete(value);

    debugPerformance.hashValuesCount = hashPrimitiveCache.size;
}
